import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { INTERIOR_PAINT_COLORS } from '@/constants/interiorDesigner';

import { imageSrcSet, imageUrl, type LandingImage, roomDesign } from './assets';
import { SPACE_GROUPS, STAGING_STEPS } from './content';
import { useInteriorLandingT, useRoomVars, useStartInterior } from './kit';
import { roomPath } from './pages';
import { findInteriorRoom } from './rooms';
import type { InteriorLandingPage } from './types';

/** The sections only one page has, after the hero. */
export default function PageDetails({ page }: { page: InteriorLandingPage }) {
  if (page.id === 'virtual-staging') return <StagingStory />;
  if (page.id === 'paint-visualizer') return <PaintPalette page={page} />;
  if (page.id === 'landscape')
    return <SpaceIdeas page={page} group='outdoor' />;
  if (page.id === 'exterior')
    return <SpaceIdeas page={page} group='exterior' />;
  return null;
}

function Picture({
  image,
  alt,
  sizes,
  className,
}: {
  image: LandingImage;
  alt: string;
  sizes: string;
  className?: string;
}) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imageUrl(image, 640)}
      srcSet={imageSrcSet(image)}
      sizes={sizes}
      alt={alt}
      loading='lazy'
      decoding='async'
      className={clsxm('h-full w-full object-cover', className)}
    />
  );
}

/** Furnished listing photo, emptied by AI, then staged: one real sequence. */
function StagingStory() {
  const { t } = useInteriorLandingT();
  const reveal = useReveal<HTMLDivElement>(0.15);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={t('Clear it out,')}
          tail={t('then stage it.')}
          lead={t(
            'Old furniture in the photo? Empty room takes it out and keeps the walls, floors and windows. Then stage the empty space in any style.',
          )}
        />
        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-6 sm:grid-cols-3 sm:gap-4 lg:gap-6',
          )}
        >
          {STAGING_STEPS.map((step, index) => (
            <figure
              key={step.label}
              className={styles.rv}
              style={delay(index * 120)}
            >
              <div className='relative aspect-[4/3] overflow-hidden rounded-[20px] bg-[#e9e9f2] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#1b1b28] dark:ring-white/10'>
                <Picture
                  image={step.image}
                  alt={t(step.label)}
                  sizes='(min-width: 640px) 32vw, 92vw'
                />
                <span className='absolute start-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white font-mono text-xs font-semibold text-[#10101c] shadow-sm'>
                  {index + 1}
                </span>
              </div>
              <figcaption
                className={clsxm('mt-3 text-[15px] font-medium', tone.ink)}
              >
                {t(step.label)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/** The ready colors, each one click away in the designer. */
function PaintPalette({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const start = useStartInterior(page);
  const reveal = useReveal<HTMLDivElement>(0.15);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={t('Start with a color,')}
          tail={t('or name your own.')}
          lead={t(
            'These colors are one click away in the designer. Any other color works too: type its name or describe it.',
          )}
        />
        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4',
          )}
        >
          {INTERIOR_PAINT_COLORS.map((paint, index) => (
            <button
              key={paint.label}
              type='button'
              onClick={() => start({ surface: 'color', prompt: paint.label })}
              className={clsxm(
                styles.rv,
                'group overflow-hidden rounded-[20px] bg-white text-start ring-1 ring-[#0f0f28]/[0.07] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-20px_rgba(22,12,90,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-white/[0.04] dark:ring-white/10',
              )}
              style={delay((index % 4) * 70)}
            >
              <span
                className='block h-24 sm:h-28'
                style={{ backgroundColor: paint.hex }}
              />
              <span className='flex items-center justify-between gap-2 px-4 py-3'>
                <span className={clsxm('text-[15px] font-semibold', tone.ink)}>
                  {t(paint.label)}
                </span>
                <span
                  className={clsxm('font-mono text-xs uppercase', tone.quiet)}
                >
                  {paint.hex}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Finished designs of outdoor spaces, each linking to its own page. */
function SpaceIdeas({
  page,
  group,
}: {
  page: InteriorLandingPage;
  group: keyof typeof SPACE_GROUPS;
}) {
  const { t } = useInteriorLandingT();
  const roomVars = useRoomVars();
  const reveal = useReveal<HTMLDivElement>(0.1);
  const rooms = SPACE_GROUPS[group]
    .map((slug) => findInteriorRoom(slug))
    .filter((room): room is NonNullable<typeof room> => Boolean(room))
    .filter((room) => roomPath(room.slug) !== page.path);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={
            group === 'outdoor'
              ? t('Ideas for every outdoor space,')
              : t('More than the house itself,')
          }
          tail={
            group === 'outdoor'
              ? t('from patios to pools.')
              : t('the whole front of your home.')
          }
          lead={t(
            'Open any space for ideas made for it, then upload your own photo to see them on your home.',
          )}
        />
        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4',
          )}
        >
          {rooms.map((room, index) => (
            <Link
              key={room.slug}
              href={roomPath(room.slug)}
              className={clsxm(styles.rv, 'group block')}
              style={delay((index % 4) * 70)}
            >
              <span className='block overflow-hidden rounded-[20px] ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/10'>
                <Picture
                  image={roomDesign(room.slug)}
                  alt={t(
                    '{{room}} design idea made with AI',
                    roomVars(room.name),
                  )}
                  sizes='(min-width: 1024px) 290px, (min-width: 640px) 31vw, 46vw'
                  className='aspect-square transition-transform duration-700 ease-out group-hover:scale-[1.04]'
                />
              </span>
              <span
                className={clsxm(
                  'mt-3 flex items-center gap-1.5 text-[15px] font-semibold',
                  tone.ink,
                )}
              >
                {t('AI {{room}} Design', roomVars(room.name))}
                <ArrowGlyph className='h-3.5 w-3.5 text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-400' />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
